import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronUpIcon, ChevronDownIcon, ShieldCheckIcon, StarIcon } from 'lucide-react';
function LiveViewersBadge() {
  const [viewers, setViewers] = useState(24);
  useEffect(() => {
    const interval = setInterval(() => {
      setViewers(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const next = prev + change * Math.ceil(Math.random() * 3);
        return Math.max(18, Math.min(42, next));
      });
    }, 3000 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, []);
  return <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4 flex items-center gap-2" data-id="element-389">
      <span className="relative flex h-2.5 w-2.5" data-id="element-390">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-391"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" data-id="element-392"></span>
      </span>
      <span className="text-sm font-semibold text-gray-800" data-id="element-393">
        <span className="font-bold text-red-600" data-id="element-394">{viewers}</span> personas están
        viendo esta página
      </span>
    </div>;
}
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 1,
    minutes: 36,
    seconds: 39
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        seconds -= 1;
        if (seconds < 0) {
          seconds = 59;
          minutes -= 1;
        }
        if (minutes < 0) {
          minutes = 59;
          hours -= 1;
        }
        if (hours < 0) {
          hours = 0;
          minutes = 0;
          seconds = 0;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 mb-5 text-center" data-id="element-395">
      <p className="text-sm font-bold text-red-500 mb-1" data-id="element-396">
        ⏰ ¡Oferta por tiempo limitado!
      </p>
      <p className="text-2xl font-extrabold text-gray-900 tracking-wide" data-id="element-397">
        {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m {pad(timeLeft.seconds)}s
      </p>
      <p className="text-xs text-gray-500 mt-1" data-id="element-398">
        Esta oferta desaparece al terminar el contador.
      </p>
    </div>;
}
function TrustBlock({
  open,
  onToggle
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return <div className="mt-6 space-y-4" data-id="element-399">
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-400">
        <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors" data-id="element-401">
          <span className="flex items-center gap-2 font-bold text-gray-900" data-id="element-402">
            <span className="text-lg" data-id="element-403">🏆</span>
            Garantía de satisfacción 7 días
          </span>
          {open ? <ChevronUpIcon className="w-5 h-5 text-gray-500" data-id="element-404" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-405" />}
        </button>
        {open && <div className="px-5 pb-5 text-left text-sm text-gray-600 leading-relaxed" data-id="element-406">
            <p className="mb-3" data-id="element-407">
              Si aplicás el contenido y sentís que no te aporta claridad o no es
              para vos, podés{' '}
              <strong className="text-gray-900" data-id="element-408">
                solicitar una devolución dentro de los primeros 7 días.
              </strong>
            </p>
            <p data-id="element-409">Quiero que compres segura/o, no con dudas.</p>
          </div>}
      </div>

      <fieldset className="border border-gray-200 rounded-xl px-5 py-4" data-id="element-410">
        <legend className="flex items-center gap-2 px-2 text-sm font-semibold text-gray-600" data-id="element-411">
          <ShieldCheckIcon className="w-4 h-4 text-brand-green" data-id="element-412" />
          Pago seguro garantizado
        </legend>
        <img src="https://cdn.magicpatterns.com/uploads/hzgvUoMWhjNHxhRDn2LPwT/Imagen_28-3-26_a_las_5.16_p._m._(1).jpg" alt="Métodos de pago" className="w-full max-w-sm mx-auto" data-id="element-413" />
      </fieldset>
    </div>;
}
type PricingCardProps = {
  title: string;
  subtitle: string;
  price: string;
  originalPrice: string;
  discountLabel: string;
  features: {
    icon: string;
    text: string;
    highlight?: boolean;
  }[];
  ctaText: string;
  ctaHref: string;
  popular?: boolean;
  guaranteeOpen: boolean;
  onToggleGuarantee: () => void;
};
function PricingCard({
  title,
  subtitle,
  price,
  originalPrice,
  discountLabel,
  features,
  ctaText,
  ctaHref,
  popular,
  guaranteeOpen,
  onToggleGuarantee
}: PricingCardProps) {
  return <div className={`relative w-full bg-white rounded-2xl overflow-hidden shadow-lg flex flex-col ${popular ? 'border-2 border-brand-green shadow-brand-green/20 ring-4 ring-brand-green/10' : 'border border-gray-200 shadow-brand-green/5'}`} data-id="element-414">
      {popular && <div className="bg-brand-green text-white text-center py-2 text-xs font-extrabold uppercase tracking-widest" data-id="element-415">
          ⭐ Más Popular · Recomendado
        </div>}

      <div className="px-6 py-8 flex flex-col flex-1" data-id="element-416">
        {/* SúperOferta + Discount */}
        <div className="flex items-center gap-3 mb-3" data-id="element-417">
          <span className="inline-block bg-red-500 text-white text-xs font-extrabold px-3 py-1 rounded-md" data-id="element-418">
            SúperOferta
          </span>
          <span className="text-red-500 font-bold text-sm animate-[pulse_0.8s_ease-in-out_infinite]" data-id="element-419">
            · {discountLabel}
          </span>
        </div>

        {/* Downloads count */}
        <p className="text-sm font-semibold text-gray-500 mb-2" data-id="element-420">
          +2173 DESCARGAS
        </p>

        {/* Product title */}
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-421">
          {title} <span className="font-normal text-gray-600" data-id="element-422">{subtitle}</span>
        </h3>

        {/* Price */}
        <div className="flex items-baseline gap-3 mb-5" data-id="element-423">
          <span className="text-2xl font-bold text-gray-900" data-id="element-424">${price}</span>
          <span className="text-lg text-gray-400 line-through" data-id="element-425">
            ${originalPrice}
          </span>
        </div>

        <hr className="border-gray-100 mb-5" data-id="element-426" />

        {/* Star rating */}
        <div className="flex items-center gap-1.5 mb-4" data-id="element-427">
          <div className="flex" data-id="element-428">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-429" />)}
          </div>
          <span className="text-sm text-gray-700 font-semibold" data-id="element-430">4.8/5</span>
          <span className="text-sm text-gray-500" data-id="element-431">de +1.300 emprendedores</span>
        </div>

        {/* Features list */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-4 mb-4 space-y-2.5" data-id="element-432">
          {features.map((f, i) => <p key={i} className={`text-sm flex items-start gap-2 ${f.highlight ? 'text-brand-green font-extrabold' : 'text-gray-800 font-bold'}`} data-id="element-433">
              <span data-id="element-434">{f.icon}</span>
              {f.text}
            </p>)}
        </div>

        <LiveViewersBadge data-id="element-435" />

        <p className="text-center text-sm font-bold text-red-500 mb-4" data-id="element-436">
          🔥 BONIFICACIÓN ACTIVA POR TIEMPO LIMITADO
        </p>

        <CountdownTimer data-id="element-437" />

        {/* CTA */}
        <a href={ctaHref} target="_blank" rel="noopener noreferrer" className={`block w-full text-white font-bold text-lg py-4 rounded-xl transition-colors duration-200 shadow-lg text-center mt-auto ${popular ? 'bg-brand-green hover:bg-brand-green-hover shadow-brand-green/30' : 'bg-gray-900 hover:bg-gray-800 shadow-gray-900/20'}`} data-id="element-438">
          {ctaText}
        </a>

        <TrustBlock open={guaranteeOpen} onToggle={onToggleGuarantee} data-id="element-439" />
      </div>
    </div>;
}
type MembershipCardProps = {
  headerLabel: string;
  preTitle?: string;
  title: React.ReactNode;
  features: {
    icon: string;
    label: React.ReactNode;
    valuation: string;
  }[];
  price: string;
  priceUnit: string;
  priceLabel: string;
  ctaText: string;
  ctaHref: string;
  guaranteeOpen: boolean;
  onToggleGuarantee: () => void;
};
function MembershipCard({
  headerLabel,
  preTitle,
  title,
  features,
  price,
  priceUnit,
  priceLabel,
  ctaText,
  ctaHref,
  guaranteeOpen,
  onToggleGuarantee
}: MembershipCardProps) {
  return <div className="w-full" data-id="element-440">
      {(preTitle || title) && <div className="text-center mb-8 max-w-3xl mx-auto" data-id="element-441">
          {preTitle && <p className="text-2xl md:text-3xl font-extrabold text-brand-green mb-2" data-id="element-442">
              {preTitle}
            </p>}
          {title && <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight" data-id="element-443">
              {title}
            </h3>}
        </div>}

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg" data-id="element-444">
        <div className="bg-gray-900 py-3 text-center" data-id="element-445">
          <span className="text-white text-sm font-bold uppercase tracking-[0.2em]" data-id="element-446">
            {headerLabel}
          </span>
        </div>

        <div className="px-6 md:px-10 py-8" data-id="element-447">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-448">
            <ul className="space-y-4 text-sm md:text-base" data-id="element-449">
              {features.map((f, i) => <li key={i} className="flex items-start gap-3" data-id="element-450">
                  <span className="text-xl" data-id="element-451">{f.icon}</span>
                  <span className="text-gray-800" data-id="element-452">
                    {f.label}{' '}
                    <span className="text-brand-green font-semibold" data-id="element-453">
                      ({f.valuation})
                    </span>
                  </span>
                </li>)}
            </ul>

            <div className="text-center" data-id="element-454">
              <div className="mb-4" data-id="element-455">
                <p className="flex items-baseline justify-center gap-1" data-id="element-456">
                  <span className="text-lg text-gray-500 self-start mt-2" data-id="element-457">
                    $
                  </span>
                  <span className="text-5xl md:text-6xl font-extrabold text-gray-900" data-id="element-458">
                    {price}
                  </span>
                  <span className="text-base text-gray-500 ml-1" data-id="element-459">
                    {priceUnit}
                  </span>
                </p>
                <p className="text-sm font-semibold text-brand-green mt-3" data-id="element-460">
                  {priceLabel}
                </p>
              </div>

              <a href={ctaHref} target="_blank" rel="noopener noreferrer" className="block w-full bg-gray-900 hover:bg-gray-800 text-white font-bold text-lg py-4 rounded-xl transition-colors duration-200 shadow-lg shadow-gray-900/20 text-center" data-id="element-461">
                {ctaText}
              </a>

              <p className="text-sm text-gray-600 mt-4" data-id="element-462">
                Actualizaciones incluidas +{' '}
                <strong className="text-gray-900" data-id="element-463">toda la información</strong>
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-464">
                Pago 100% seguro · Mercado Pago
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-465">📩 Acceso inmediato</p>
            </div>
          </div>

          <TrustBlock open={guaranteeOpen} onToggle={onToggleGuarantee} data-id="element-466" />
        </div>
      </div>
    </div>;
}
export function PricingSection() {
  const [guaranteeOpen1, setGuaranteeOpen1] = useState(false);
  const [guaranteeOpen2, setGuaranteeOpen2] = useState(false);
  const [guaranteeOpen3, setGuaranteeOpen3] = useState(false);
  const [guaranteeOpen4, setGuaranteeOpen4] = useState(false);
  const [guaranteeOpen5, setGuaranteeOpen5] = useState(false);
  return <section id="pricing" className="w-full bg-brand-bg pt-6 md:pt-8 pb-8 md:pb-10 px-6" data-id="element-467">
      <div className="max-w-7xl mx-auto" data-id="element-468">
        {/* Product image */}
        <motion.div className="flex justify-center mb-10" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-469">
          <img src="https://cdn.magicpatterns.com/uploads/8T5cfanvWDbWYTKJt7Q2U9/11.png" alt="Vende ebooks con Shopify: La guía definitiva" className="w-full max-w-3xl h-auto rounded-2xl shadow-xl border border-gray-200" data-id="element-470" />
        </motion.div>

        {/* Pricing cards grid */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-471">
          {/* Card 1: Básico */}
          <PricingCard title="Vende ebooks con Shopify:" subtitle="La guía definitiva" price="24.900" originalPrice="50.000" discountLabel="50% OFF" features={[{
          icon: '📖',
          text: 'CURSO DIGITAL – ACCESO INMEDIATO'
        }, {
          icon: '📩',
          text: 'ACCESO AUTOMÁTICO AL FINALIZAR LA COMPRA'
        }, {
          icon: '🔒',
          text: 'PAGO 100% SEGURO CON MERCADO PAGO'
        }]} ctaText="🛒 Comprar ahora" ctaHref="https://www.emprende-digital.com/cart/51742249156888:1" guaranteeOpen={guaranteeOpen1} onToggleGuarantee={() => setGuaranteeOpen1(!guaranteeOpen1)} data-id="element-472" />

          {/* Card 2: Premium con Mentorías */}
          <PricingCard title="La guía definitiva +" subtitle="Acompañamiento con Mentorías Semanales" price="49.900" originalPrice="100.000" discountLabel="50% OFF" features={[{
          icon: '📖',
          text: 'TODO LO QUE INCLUYE LA GUÍA DEFINITIVA'
        }, {
          icon: '🎥',
          text: 'MENTORÍAS GRUPALES SEMANALES EN VIVO',
          highlight: true
        }, {
          icon: '💬',
          text: 'COMUNIDAD VIP DE WHATSAPP — SOPORTE 24/7',
          highlight: true
        }, {
          icon: '🎯',
          text: 'CORRECCIONES Y ESTRATEGIA PERSONALIZADA',
          highlight: true
        }, {
          icon: '🔒',
          text: 'PAGO 100% SEGURO CON MERCADO PAGO'
        }]} ctaText="🚀 Acceder con Mentorías" ctaHref="https://www.emprende-digital.com/cart/52045860733208:1" popular guaranteeOpen={guaranteeOpen2} onToggleGuarantee={() => setGuaranteeOpen2(!guaranteeOpen2)} data-id="element-473" />
        </motion.div>

        {/* Membresía y Comunidad - tier 3 */}
        <motion.div className="mt-16 md:mt-24" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-474">
          <MembershipCard headerLabel="Membresía" preTitle="Mentoría 1 a 1 Productos Digitales" title={<>
                Para quienes ya están en el proceso
                <br data-id="element-476" />y quieren avanzar al siguiente nivel{' '}
                <span className="relative inline-block" data-id="element-477">
                  MÁS RÁPIDO
                  <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-brand-green rounded-full" data-id="element-478" />
                </span>
              </>} features={[{
          icon: '📣',
          label: <>
                    <strong className="font-bold" data-id="element-479">Formación Iniciación</strong>{' '}
                    desbloquea tu potencial
                  </>,
          valuation: 'Valorado en 500 USD'
        }, {
          icon: '📣',
          label: <strong className="font-bold" data-id="element-480">
                    Mentorías semanales EN VIVO con SOLE
                  </strong>,
          valuation: 'Valor incalculable'
        }, {
          icon: '📣',
          label: <strong className="font-bold" data-id="element-481">
                    Mentorías en vivo grabadas
                  </strong>,
          valuation: 'Valorado en 700 USD'
        }, {
          icon: '🎖️',
          label: <strong className="font-bold" data-id="element-482">
                    BONUS: FORMACIÓN EN EDICIÓN DE VIDEO Y MARKETING DE
                    AFILIADOS
                  </strong>,
          valuation: 'Valorado en 1000 USD'
        }]} price="100" priceUnit="USD/mes" priceLabel="Precio lanzamiento por tiempo limitado" ctaText="🚀 Membresía" ctaHref="https://www.emprende-digital.com/cart/51868533883160:1" guaranteeOpen={guaranteeOpen3} onToggleGuarantee={() => setGuaranteeOpen3(!guaranteeOpen3)} data-id="element-475" />
        </motion.div>

        {/* Salas Premium - tier 4 */}
        <motion.div className="mt-16 md:mt-20" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-483">
          <div className="text-center mb-8 max-w-3xl mx-auto" data-id="element-484">
            <p className="text-2xl md:text-3xl font-extrabold text-brand-green mb-2" data-id="element-485">
              Salas Premium
            </p>
            <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight" data-id="element-486">
              El nivel más{' '}
              <span className="relative inline-block" data-id="element-487">
                avanzado
                <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-brand-green rounded-full" data-id="element-488" />
              </span>{' '}
              para escalar tu negocio
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start" data-id="element-489">
            <MembershipCard headerLabel="Sala Ecommerce" features={[{
            icon: '📣',
            label: <strong className="font-bold" data-id="element-491">
                      Estrategias avanzadas de Ecommerce
                    </strong>,
            valuation: 'Valorado en 800 USD'
          }, {
            icon: '📣',
            label: <strong className="font-bold" data-id="element-492">
                      Mentorías exclusivas con expertos
                    </strong>,
            valuation: 'Valor incalculable'
          }, {
            icon: '📣',
            label: <strong className="font-bold" data-id="element-493">
                      Comunidad VIP de Ecommerce
                    </strong>,
            valuation: 'Valorado en 500 USD'
          }, {
            icon: '🎖️',
            label: <strong className="font-bold" data-id="element-494">
                      Recursos, plantillas y casos reales
                    </strong>,
            valuation: 'Valorado en 700 USD'
          }]} price="900" priceUnit="USD" priceLabel="Precio lanzamiento por tiempo limitado" ctaText="🚀 Sala Ecommerce" ctaHref="#" guaranteeOpen={guaranteeOpen4} onToggleGuarantee={() => setGuaranteeOpen4(!guaranteeOpen4)} data-id="element-490" />

            <MembershipCard headerLabel="Sala Dropshipping" features={[{
            icon: '📣',
            label: <strong className="font-bold" data-id="element-496">
                      Búsqueda de productos ganadores
                    </strong>,
            valuation: 'Valorado en 800 USD'
          }, {
            icon: '📣',
            label: <strong className="font-bold" data-id="element-497">
                      Proveedores verificados y confiables
                    </strong>,
            valuation: 'Valor incalculable'
          }, {
            icon: '📣',
            label: <strong className="font-bold" data-id="element-498">
                      Estrategias de escalado avanzadas
                    </strong>,
            valuation: 'Valorado en 500 USD'
          }, {
            icon: '🎖️',
            label: <strong className="font-bold" data-id="element-499">
                      Mentorías y comunidad VIP Dropshipping
                    </strong>,
            valuation: 'Valorado en 700 USD'
          }]} price="900" priceUnit="USD" priceLabel="Precio lanzamiento por tiempo limitado" ctaText="🚀 Sala Dropshipping" ctaHref="#" guaranteeOpen={guaranteeOpen5} onToggleGuarantee={() => setGuaranteeOpen5(!guaranteeOpen5)} data-id="element-495" />
          </div>
        </motion.div>

        {/* Tienda Líder Platinum badge */}
        <motion.div className="mt-10 flex justify-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-500">
          <img src="https://cdn.magicpatterns.com/uploads/6a1pUYDihJzNUtN2uLf8oo/Imagen_29-3-26_a_las_12.25_a._m..jpg" alt="Tienda Líder Platinum" className="w-full max-w-3xl h-auto rounded-2xl shadow-md" data-id="element-501" />
        </motion.div>
      </div>
    </section>;
}